import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Clock, CheckCircle, ChevronRight, Car, Sparkles } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { serviceService } from '../services/api';
import { toast } from '../hooks/use-toast';
import { carBrands, carModels } from '../mock/mockData';
import axios from 'axios';
import { ServiceSchema, BreadcrumbSchema } from '../components/SchemaMarkup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Services = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [vehicleCategory, setVehicleCategory] = useState(null);
  const [availableBrands, setAvailableBrands] = useState(carBrands);
  const [availableModels, setAvailableModels] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchServices();
    fetchVehicles();

    const chatbotVehicleInfo = sessionStorage.getItem('chatbot_vehicle_info');
    const recommendedService = sessionStorage.getItem('recommended_service');

    if (chatbotVehicleInfo) {
      try {
        const vehicleInfo = JSON.parse(chatbotVehicleInfo);
        if (vehicleInfo.brand) setSelectedBrand(vehicleInfo.brand);
        if (vehicleInfo.model) setSelectedModel(vehicleInfo.model);
        toast({ title: 'Vehicle Info Pre-filled', description: `${vehicleInfo.brand} ${vehicleInfo.model} selected from chatbot`, duration: 3000 });
        sessionStorage.removeItem('chatbot_vehicle_info');
      } catch (e) {
        console.error('Error parsing chatbot vehicle info:', e);
      }
    }

    if (recommendedService) {
      toast({ title: 'Recommended Service', description: recommendedService, duration: 4000 });
      sessionStorage.setItem('pending_service_selection', recommendedService);
    }
  }, []);

  useEffect(() => {
    const serviceId = searchParams.get('service');
    if (serviceId && services.length > 0) {
      setActiveTab(serviceId);
      const service = services.find(s => s.id === serviceId);
      if (service) setSelectedService(service);
    } else if (services.length > 0 && !activeTab) {
      setActiveTab(services[0].id);
    }
  }, [searchParams, services]);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vehicles`);
      const vehicles = response.data.vehicles || [];
      const brands = [...new Set(vehicles.map(v => v.brand))].sort();
      setAvailableBrands(brands);
    } catch (error) {
      setAvailableBrands(carBrands);
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      const data = await serviceService.getAll();
      setServices(data.services || []);

      const pendingService = sessionStorage.getItem('pending_service_selection');
      if (data.services && data.services.length > 0) {
        if (pendingService) {
          const recommendedService = data.services.find(s => s.name === pendingService);
          if (recommendedService) {
            setSelectedService(recommendedService);
            setActiveTab(recommendedService.id);
            setTimeout(() => {
              document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 500);
          } else {
            setSelectedService(data.services[0]);
          }
          sessionStorage.removeItem('pending_service_selection');
          sessionStorage.removeItem('recommended_service');
        } else {
          setSelectedService(data.services[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      toast({ title: 'Error', description: 'Failed to load services. Please refresh the page.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleCategory = async (brand, model) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vehicles`);
      const vehicle = response.data.vehicles.find(v => v.brand === brand && v.model === model);
      if (vehicle && vehicle.category) setVehicleCategory(vehicle.category);
      else setVehicleCategory(null);
    } catch (error) {
      setVehicleCategory(null);
    }
  };

  useEffect(() => {
    if (selectedBrand) {
      const fetchModelsForBrand = async () => {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/vehicles`);
          const vehicles = response.data.vehicles || [];
          const models = vehicles
            .filter(v => v.brand === selectedBrand)
            .map(v => v.model)
            .filter((model, index, self) => self.indexOf(model) === index)
            .sort();
          setAvailableModels(models);
        } catch (error) {
          setAvailableModels(carModels[selectedBrand] || []);
        }
      };
      fetchModelsForBrand();
    } else {
      setAvailableModels([]);
    }
  }, [selectedBrand]);

  useEffect(() => {
    if (selectedBrand && selectedModel) fetchVehicleCategory(selectedBrand, selectedModel);
  }, [selectedBrand, selectedModel]);

  const getPackagePrice = (pkg) => {
    if (!selectedBrand || !selectedModel || !vehicleCategory) return null;
    if (pkg.brand_category_prices && pkg.brand_category_prices.length > 0) {
      const specificPrice = pkg.brand_category_prices.find(p => p.brand === selectedBrand && p.category === vehicleCategory);
      if (specificPrice) return specificPrice.price;
    }
    return null;
  };

  const handleBookService = (pkg) => {
    if (!selectedBrand || !selectedModel) {
      toast({ title: 'Select Your Car', description: 'Please select your car brand and model to continue', variant: 'destructive' });
      return;
    }
    const calculatedPrice = getPackagePrice(pkg);
    if (calculatedPrice === null) {
      toast({ title: 'Pricing Not Available', description: 'Pricing for your vehicle is not available. Please contact us for a quote.', variant: 'destructive' });
      return;
    }
    navigate('/booking', {
      state: {
        service: selectedService.name,
        package: { ...pkg, price: calculatedPrice },
        carBrand: selectedBrand,
        carModel: selectedModel,
        vehicleCategory
      }
    });
  };

  const breadcrumbItems = [
    { name: 'Home', url: 'https://propitstop.in/' },
    { name: 'Services', url: 'https://propitstop.in/services' }
  ];

  return (
    <div data-testid="services-page" className="min-h-screen bg-slate-950 text-slate-200">
      <BreadcrumbSchema items={breadcrumbItems} />
      {services.map(service => <ServiceSchema key={service.id} service={service} />)}

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 pt-24 pb-16">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(6,182,212,0.18),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(59,130,246,0.12),transparent_55%)]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-medium">Services</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white max-w-3xl">
            Every service your car <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">actually needs</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            Transparent pricing tailored to your exact make and model. Select your car to unlock real rates.
          </p>
        </div>
      </section>

      {/* Services Content */}
      <section id="services-section" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-400">No services available at the moment.</p>
            </div>
          ) : (
            <Tabs value={activeTab || services[0]?.id} className="w-full" onValueChange={(value) => {
              setActiveTab(value);
              const service = services.find(s => s.id === value);
              setSelectedService(service);
            }}>
              {/* Service Tabs */}
              <TabsList
                data-testid="service-tabs"
                className="grid w-full grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mb-8 h-auto bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-2 gap-1"
              >
                {services.map(service => (
                  <TabsTrigger
                    key={service.id}
                    value={service.id}
                    data-testid={`service-tab-${service.id}`}
                    className="py-3 px-4 rounded-xl font-medium text-sm text-slate-400 hover:text-white data-[state=active]:bg-cyan-500 data-[state=active]:text-slate-950 data-[state=active]:font-bold transition-colors duration-200"
                  >
                    {service.name}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Car Selector */}
              <div
                data-testid="car-selector"
                className="mb-10 bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                    <Car className="w-4 h-4 text-cyan-400" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-base font-semibold text-white">Select your car to see real pricing</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">Car Brand *</label>
                    <Select
                      value={selectedBrand}
                      onValueChange={(value) => {
                        setSelectedBrand(value);
                        setSelectedModel('');
                        setVehicleCategory(null);
                      }}
                    >
                      <SelectTrigger data-testid="brand-select" className="bg-slate-950/60 border-slate-700 text-white hover:border-cyan-500/40">
                        <SelectValue placeholder="Select brand" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 max-h-[300px]">
                        {availableBrands.map(brand => (
                          <SelectItem key={brand} value={brand} className="text-slate-200 focus:bg-slate-800 focus:text-white">{brand}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">Car Model *</label>
                    <Select value={selectedModel} onValueChange={setSelectedModel} disabled={!selectedBrand}>
                      <SelectTrigger data-testid="model-select" className="bg-slate-950/60 border-slate-700 text-white disabled:opacity-50 hover:border-cyan-500/40">
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 max-h-[300px]">
                        {availableModels.map(model => (
                          <SelectItem key={model} value={model} className="text-slate-200 focus:bg-slate-800 focus:text-white">{model}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {selectedBrand && selectedModel && vehicleCategory && (
                  <div className="mt-4 p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    <p className="text-sm text-cyan-200">
                      Showing prices for <strong className="text-white">{selectedBrand} {selectedModel}</strong> <span className="text-slate-400">· {vehicleCategory}</span>
                    </p>
                  </div>
                )}
                {selectedBrand && selectedModel && !vehicleCategory && (
                  <div className="mt-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                    <p className="text-sm text-yellow-300">Loading pricing information...</p>
                  </div>
                )}
              </div>

              {services.map(service => (
                <TabsContent key={service.id} value={service.id} className="space-y-10">
                  {/* Service Header */}
                  <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-slate-900/40 backdrop-blur-xl">
                    <div className="grid md:grid-cols-2 gap-0">
                      <div className="relative h-64 md:h-auto min-h-[260px]">
                        <img src={service.image} alt={service.name} className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-r md:bg-gradient-to-r from-transparent to-slate-900/60 md:to-slate-900/80" />
                      </div>
                      <div className="p-8 md:p-10 flex flex-col justify-center">
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight mb-4">{service.name}</h2>
                        <p className="text-slate-400 leading-relaxed mb-6">{service.description}</p>
                        <div className="flex flex-wrap gap-3">
                          {['Genuine Parts', 'Expert Mechanics', '6 Month Warranty'].map((badge) => (
                            <div key={badge} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-950/60 border border-slate-700/60">
                              <CheckCircle className="w-3.5 h-3.5 text-cyan-400" />
                              <span className="text-xs text-slate-300">{badge}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Packages */}
                  <div>
                    <div className="flex items-end justify-between mb-6">
                      <div>
                        <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">Packages</span>
                        <h3 className="mt-1 text-2xl font-extrabold text-white tracking-tight">Choose what fits</h3>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      {service.packages.map((pkg, index) => {
                        const price = getPackagePrice(pkg);
                        const isReady = !!selectedBrand && !!selectedModel;
                        return (
                          <div
                            key={index}
                            data-testid={`package-card-${index}`}
                            className="group relative bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 hover:border-cyan-500/40 hover:-translate-y-1 transition-[transform,border-color,box-shadow] duration-300 hover:shadow-[0_0_25px_rgba(6,182,212,0.15)]"
                          >
                            <h4 className="text-lg font-bold text-white mb-3">{pkg.name}</h4>

                            {price !== null ? (
                              <div className="flex items-baseline gap-1 mb-4">
                                <span className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 tracking-tight">₹{price}</span>
                                <span className="text-xs text-slate-500">onwards</span>
                              </div>
                            ) : (
                              <div className="mb-4 p-3 rounded-xl bg-slate-950/60 border border-slate-700/50">
                                <p className="text-xs text-slate-400">Select your car above to see pricing</p>
                              </div>
                            )}

                            {pkg.description && (
                              <div className="mb-4 text-sm text-slate-300 space-y-1.5">
                                {pkg.description.split('\n').filter(line => line.trim()).slice(0, 6).map((line, idx) => (
                                  <div key={idx} className="flex items-start gap-2">
                                    <CheckCircle className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0 mt-1" />
                                    <span className="flex-1 text-slate-400">
                                      {line.trim().startsWith('✅') ? line.trim().slice(1).trim() : line.trim()}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="flex items-center gap-2 text-slate-500 mb-5">
                              <Clock className="w-3.5 h-3.5" />
                              <span className="text-xs">{pkg.duration}</span>
                            </div>

                            <Button
                              onClick={() => handleBookService(pkg)}
                              disabled={!isReady}
                              data-testid={`book-now-btn-${index}`}
                              className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl disabled:bg-slate-800 disabled:text-slate-500 disabled:hover:bg-slate-800 shadow-[0_0_12px_rgba(6,182,212,0.15)] hover:shadow-[0_0_22px_rgba(6,182,212,0.35)] transition-[box-shadow,background-color] duration-300"
                            >
                              {isReady ? <>Book Now <ChevronRight className="w-4 h-4 ml-1" /></> : 'Select Car to Book'}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </section>

      {/* Stats */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 border border-slate-800 px-8 py-14 md:px-16 md:py-16">
            <div className="absolute inset-0 opacity-40 pointer-events-none">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_30%,rgba(6,182,212,0.22),transparent_55%)]" />
            </div>
            <div className="relative grid md:grid-cols-3 gap-10">
              {[
                { num: '50K+', label: 'Services Completed' },
                { num: '4.8/5', label: 'Average Rating' },
                { num: '24/7', label: 'Customer Support' }
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 tracking-tight">{s.num}</div>
                  <div className="mt-2 text-sm uppercase tracking-[0.2em] text-slate-400">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
